const contactFieldLabels = {
  "official.first_name": "Authorized Official First Name",
  "official.last_name": "Authorized Official Last Name",
  "official.title": "Authorized Official Job Title",
  "official.phone_number": "Authorized Official Phone Number",
  "official.email_address": "Authorized Official Email Address",
  "official.fax_number": "Authorized Official Fax Number",
  "primary.first_name": "Primary Contact First Name",
  "primary.last_name": "Primary Contact Last Name",
  "primary.title": "Primary Contact Job Title",
  "primary.phone_number": "Primary Contact Phone Number",
  "primary.email_address": "Primary Contact Email Address",
  "secondary.first_name": "Secondary Contact First Name",
  "secondary.last_name": "Secondary Contact Last Name",
  "secondary.title": "Secondary Contact Job Title",
  "secondary.phone_number": "Secondary Contact Phone Number",
  "secondary.email_address": "Secondary Contact Email Address",
} as const;

export type ContactInformationField = keyof typeof contactFieldLabels;

const serverFieldMap: Record<string, ContactInformationField> = {
  authorized_official_first_name: "official.first_name",
  authorized_official_last_name: "official.last_name",
  authorized_official_title: "official.title",
  authorized_official_phone: "official.phone_number",
  authorized_official_email: "official.email_address",
  authorized_official_fax: "official.fax_number",
  primary_contact_first_name: "primary.first_name",
  primary_contact_last_name: "primary.last_name",
  primary_contact_title: "primary.title",
  primary_contact_phone: "primary.phone_number",
  primary_contact_email: "primary.email_address",
  secondary_contact_first_name: "secondary.first_name",
  secondary_contact_last_name: "secondary.last_name",
  secondary_contact_title: "secondary.title",
  secondary_contact_phone: "secondary.phone_number",
  secondary_contact_email: "secondary.email_address",
};

export const sameAsPrimaryContactMessage = "Cannot be the same as Primary Contact";
export const sameAsAuthorizedOfficialMessage = "Cannot be the same as Authorized Official";
export const sameAsSecondaryContactMessage = "Cannot be the same as Secondary Contact";
export const sameAsAuthorizedOfficialFaxNumberMessage = "Cannot be the same as Authorized Official Fax Number";
export const sameAsAuthorizedOfficialPhoneNumberMessage = "Cannot be the same as Authorized Official Phone Number";
export const sameAsPrimaryContactPhoneNumberMessage = "Cannot be the same as Primary Contact Phone Number";
export const sameAsSecondaryContactPhoneNumberMessage = "Cannot be the same as Secondary Contact Phone Number";

const sameContactErrorMessages = new Set([
  sameAsPrimaryContactMessage,
  sameAsAuthorizedOfficialMessage,
  sameAsSecondaryContactMessage,
  sameAsAuthorizedOfficialFaxNumberMessage,
  sameAsAuthorizedOfficialPhoneNumberMessage,
  sameAsPrimaryContactPhoneNumberMessage,
  sameAsSecondaryContactPhoneNumberMessage,
]);

export const contactInformationErrorMessage =
  "Failed to save contact information. Please try again later.";

const sameContactNameMessage = "Contact names must be different.";
const sameContactPhoneMessage = "Contact phone and fax numbers must be different.";
const sameContactEmailMessage = "Contact email addresses must be different.";

const sameContactMessages: Partial<Record<ContactInformationField, string>> = {
  "official.first_name": sameContactNameMessage,
  "official.last_name": sameContactNameMessage,
  "primary.first_name": sameContactNameMessage,
  "primary.last_name": sameContactNameMessage,
  "secondary.first_name": sameContactNameMessage,
  "secondary.last_name": sameContactNameMessage,
  "official.phone_number": sameContactPhoneMessage,
  "official.fax_number": sameContactPhoneMessage,
  "primary.phone_number": sameContactPhoneMessage,
  "secondary.phone_number": sameContactPhoneMessage,
  "official.email_address": sameContactEmailMessage,
  "primary.email_address": sameContactEmailMessage,
  "secondary.email_address": sameContactEmailMessage,
};

const isContactInformationField = (
  path: string,
): path is ContactInformationField => {
  return path in contactFieldLabels;
};

const isNestedErrorRecord = (
  value: unknown,
): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

export const getServerFormField = (
  field: string,
): ContactInformationField | undefined => {
  return serverFieldMap[field];
};

export const getFieldBannerMessage = (path: string, message: string): string => {
  if (!isContactInformationField(path)) {
    return message;
  }

  const sameContactMessage = sameContactMessages[path];

  if (sameContactMessage && sameContactErrorMessages.has(message)) {
    return sameContactMessage;
  }

  return `${contactFieldLabels[path]}: ${message}`;
};

export const getBannerMessages = (
  errors: Record<string, unknown>,
  prefix = "",
): string[] => {
  const bannerMessages = new Set<string>();

  Object.entries(errors).forEach(([key, value]) => {
    if (!isNestedErrorRecord(value)) {
      return;
    }

    const nextPath = prefix ? `${prefix}.${key}` : key;
    const message = value.message;

    if (typeof message === "string") {
      bannerMessages.add(getFieldBannerMessage(nextPath, message));
      return;
    }

    getBannerMessages(value, nextPath).forEach((nestedMessage) => {
      bannerMessages.add(nestedMessage);
    });
  });

  return Array.from(bannerMessages);
};
